import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  GetCreditReversalsParams,
  UpdateCreditReversalPayload,
  UpdateCreditReversalResponse,
  getCreditReversals,
  updateCreditReversal,
} from '~/services/CreditReversalService';

const CREDIT_REVERSAL_MANAGEMENT_BASE_KEY = 'creditReversalManagement' as const;

export const creditReversalManagementQueryKeys = {
  all: [CREDIT_REVERSAL_MANAGEMENT_BASE_KEY] as const,
  lists: () => [...creditReversalManagementQueryKeys.all, 'list'] as const,
  list: (params: GetCreditReversalsParams) => [...creditReversalManagementQueryKeys.lists(), params] as const,
};

export function useGetCreditReversals(params: GetCreditReversalsParams) {
  return useQuery({
    queryKey: creditReversalManagementQueryKeys.list(params),
    queryFn: ({ signal }) => getCreditReversals(params, signal),
    // Without this, changing page clears `data` until the fetch finishes; `rowCount` becomes 0 and
    // MUI DataGrid resets the page (rowCountChange → setPage(0)), aborting the new request.
    placeholderData: keepPreviousData,
  });
}

export function useUpdateCreditReversal() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<UpdateCreditReversalResponse, Error, { id: string; payload: UpdateCreditReversalPayload }>({
    mutationFn: ({ id, payload }) => updateCreditReversal(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditReversalManagementQueryKeys.lists() });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update credit reversal: ${error.message}`, { variant: 'error' });
    },
  });
}
